import {Injectable} from '@angular/core';
import {ConfigService} from '../config/config.service';

/**
 * Global object declaration
 */
declare global {
  /**
   * Gets the window object to inject the Google script onto
   */
  interface Window { dataLayer: any }
}
/**
 * Window datalayer to keep types happy
 */
window.dataLayer = window.dataLayer || [];

/**
 * Events category to keep track of
 */
type EventCategoryType = 'widget' | 'layer' | 'page_view' | 'dropdown' | 'attributeTable';

/**
 * Event actions to keep track of
 */
type EventActionType = 'view-item' | 'select-content' | 'page-change' | 'download' | 'action' | 'dropdown';

/**
 * Simplified Google Analytics API
 */
@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {

  /**
   * Constructor function to inject app variables into the viewer
   *
   * @param config
   */
  constructor(private config: ConfigService) {}

  /**
   * This function adds the Google Analytics script to the main html page
   * The reason it needs to be here is to get the gtag out of the config file after startup
   */
  public init() {
    try {
      if (this.config.gtag) {
        const script = document.createElement('script');
        script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${this.config.gtag}');
      `;
        document.head.appendChild(script);
      }
    } catch (ex) {
      console.log('Error appending google analytics');
      console.log(ex);
    }
  }

  /**
   * Sends positive analytics to keep track of
   *
   * @param eventCategory
   * @param eventAction
   * @param eventLabel
   * @param value
   */
  public eventEmitter(
    eventCategory: EventCategoryType,
    eventAction: EventActionType,
    eventLabel: string = null,
    value: any = null) {
    if (window.dataLayer) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      window.dataLayer.push({event: eventAction, event_action: eventLabel,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        event_category: eventCategory, event_label: eventLabel, value});
    }
  }

  /**
   * Sends error info to Google Analytics to keep track of
   *
   * @param error
   * @param exFatal
   */
  public exception(error, exFatal) {
    let name = error;
    if (error.name) {
      name = error.name;
    } else if (error.description) {
      name = error.description;
    }
    if (window.dataLayer) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      window.dataLayer.push({event: 'exception', event_category: 'exceptionCaught' , description: name, fatal: exFatal});
    }
    if (!exFatal) {
      console.warn(error);
    } else {
      console.error(error);
      alert('We are sorry, something went wrong. Please reload the page or try again later. Problem:' + name);
    }
  }

  /**
   * Keeps track of load time
   */
  public timing() {
    if (window.dataLayer) {
      window.dataLayer.push(
        // eslint-disable-next-line @typescript-eslint/naming-convention
        {event: 'timing_complete', name: 'load', value: Math.round(performance.now()), event_category: 'JS Dependencies'}
        );
    }
  }
}
